import {FormControl, FormField} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {BaseVehicleClass, useGetVehicleClassesQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  MakeSelect,
  ModelSelect,
  monthOptions,
  useVehicleCatalogue,
  VinDecoderField,
  yearOptionsWithFuture,
} from '@dms/shared';

import {handleFormReset, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleDetailsFormType} from '../types';
import {IdentifiedVehicles} from './IdentifiedVehicles';

interface VehicleDetailsFormProps extends RequiredTestIdProps {
  control: FormControl<VehicleDetailsFormType>;
  formApi: UseFormReturn<VehicleDetailsFormType>;
  onVehicleSelect: (vehicleId: string) => void;
  selectingVehicleId?: string | null;
}

export function VehicleDetailsForm(props: VehicleDetailsFormProps) {
  const [vehicleTypes, make, licensePlate, vin] = useWatch({
    control: props.control,
    name: ['vehicleType', 'make', 'licensePlate', 'vin'],
  });

  const vehicleType = vehicleTypes?.[0] ?? 'VEHICLETYPE_PASSENGER_CAR';

  const [vehicleUtils] = useVehicleCatalogue(vehicleType, false);

  const {data: vehicleClasses} = useGetVehicleClassesQuery();
  const vehicleClassOptions =
    vehicleClasses
      ?.filter((vc: BaseVehicleClass) => vc.isActive)
      .map((vc: BaseVehicleClass) => ({value: vc.vehicleClassId, label: vc.name})) ?? [];

  return (
    <VStack spacing={4}>
      <VinDecoderField<VehicleDetailsFormType>
        control={props.control}
        formApi={props.formApi}
        registrationPlateField={{
          name: 'licensePlate',
          label: i18n.t('entity.vehicle.labels.licensePlate'),
        }}
        vinField={{
          name: 'vin',
          label: i18n.t('entity.vehicle.labels.vin'),
        }}
        isStatic
        data-testid={suffixTestId('vinDecoder', props)}
      />
      <IdentifiedVehicles
        vin={vin}
        registrationPlate={licensePlate}
        onVehicleSelect={props.onVehicleSelect}
        selectingVehicleId={props.selectingVehicleId}
        data-testid={suffixTestId('identifiedVehicles', props)}
      />
      <FormField
        control={props.control}
        type="chips"
        name="vehicleType"
        options={vehicleUtils.vehicleTypeOptions ?? []}
        label={i18n.t('entity.vehicle.labels.vehicleType')}
        isRequired
        onChange={() =>
          handleFormReset<VehicleDetailsFormType>(props.formApi, [
            {name: 'make', value: null},
            {name: 'modelFamily', value: null},
            {name: 'trim', value: null},
            {name: 'variant', value: null},
          ])
        }
        data-testid={suffixTestId('vehicleType', props)}
      />
      <Grid columns={4}>
        <MakeSelect<VehicleDetailsFormType>
          vehicleType={vehicleType}
          control={props.control}
          name="make"
          label={i18n.t('entity.vehicle.labels.make')}
          onChange={() =>
            handleFormReset<VehicleDetailsFormType>(props.formApi, [
              {name: 'modelFamily', value: null},
              {name: 'trim', value: null},
              {name: 'variant', value: null},
            ])
          }
          data-testid={suffixTestId('make', props)}
        />
        <ModelSelect<VehicleDetailsFormType>
          vehicleType={vehicleType}
          makes={make}
          control={props.control}
          name="modelFamily"
          label={i18n.t('entity.vehicle.labels.model')}
          data-testid={suffixTestId('model', props)}
        />
        <FormField
          control={props.control}
          type="text"
          name="trim"
          label={i18n.t('entity.vehicle.labels.trimLevel')}
          data-testid={suffixTestId('trim', props)}
        />
        <FormField
          control={props.control}
          type="text"
          name="variant"
          label={i18n.t('entity.vehicle.labels.variantName')}
          data-testid={suffixTestId('variant', props)}
        />
      </Grid>
      <Grid columns={4}>
        <FormField
          control={props.control}
          type="apiDate"
          name="firstRegistration"
          label={i18n.t('entity.vehicle.labels.firstRegistration')}
          data-testid={suffixTestId('firstRegistration', props)}
        />
        <FormField
          control={props.control}
          type="choice"
          name="realProductionYear"
          options={yearOptionsWithFuture}
          label={i18n.t('entity.vehicle.labels.realProductionYear')}
          data-testid={suffixTestId('realProductionYear', props)}
        />
        <FormField
          control={props.control}
          type="choice"
          name="realProductionMonth"
          options={monthOptions}
          label={i18n.t('entity.vehicle.labels.realProductionMonth')}
          data-testid={suffixTestId('realProductionMonth', props)}
        />
        <FormField
          control={props.control}
          type="choice"
          name="vehicleClassId"
          options={vehicleClassOptions}
          label={i18n.t('entity.vehicleClass.labels.vehicleClass')}
          data-testid={suffixTestId('vehicleClass', props)}
        />
      </Grid>
    </VStack>
  );
}
